import { cloneSystemData, validateSystemData } from '../../plugin-contracts/system_data_contract.mjs';

function freezeData(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(freezeData);
    Object.freeze(value);
  }
  return value;
}

function syncResult(value) {
  if (value && typeof value.then === 'function') {
    Promise.resolve(value).catch(() => undefined);
    throw new TypeError('Systembefehle muessen synchron abgeschlossen werden.');
  }
  return value;
}

/** Owner-scoped commands reuse the application's snapshot history. No event log. */
export function createSystemCommandService(contract, ports) {
  let activeDraft = null;
  let draftActive = false;
  let readCache = null;
  let gesture = null;

  function context() {
    return { project: ports.getProject(), sheetId: ports.getSheetId() };
  }
  function isCurrent(captured) {
    return captured.project === ports.getProject() && captured.sheetId === ports.getSheetId();
  }
  function discardStaleGesture() {
    // A sheet/project switch owns its history. Never restore data into that new context.
    if (gesture && !isCurrent(gesture.captured)) gesture = null;
  }
  function rawData(project = ports.getProject()) {
    let data = project;
    for (const segment of contract.path) data = data?.[segment];
    return data === undefined ? contract.defaults : data;
  }
  function assignData(project, data) {
    let target = project;
    for (const segment of contract.path.slice(0, -1)) {
      if (!target[segment]) target[segment] = {};
      target = target[segment];
    }
    target[contract.path.at(-1)] = data;
    project.systemDataVersions = { ...(project.systemDataVersions || {}), [contract.systemId]: contract.version };
    readCache = null;
  }
  function read() {
    if (draftActive) return activeDraft;
    const project = ports.getProject();
    const raw = rawData(project);
    const revision = ports.getRevision();
    if (!readCache || readCache.project !== project || readCache.raw !== raw || readCache.revision !== revision) {
      readCache = { project, raw, revision, value: freezeData(cloneSystemData(raw)) };
    }
    return readCache.value;
  }
  function prepare(update) {
    if (ports.isExternallyBusy?.()) throw new Error('Ein Auftragsbefehl wird vorbereitet.');
    if (typeof update !== 'function') throw new TypeError('Systembefehl benoetigt eine Mutation.');
    if (draftActive) return { nested: true, result: syncResult(update(activeDraft)) };
    if (!ports.authorize()) throw new Error('Keine Berechtigung fuer diese Systemaenderung.');
    const captured = context();
    if (!captured.project) throw new Error('Kein aktives Projekt.');
    const before = rawData(captured.project);
    activeDraft = cloneSystemData(before);
    draftActive = true;
    try {
      const result = syncResult(update(activeDraft));
      if (result === false) return { result, changed: false };
      validateSystemData(contract, activeDraft, contract.version);
      const next = cloneSystemData(activeDraft);
      if (!isCurrent(captured)) throw new Error('Projektkontext waehrend des Befehls gewechselt.');
      return { captured, next, result, changed: JSON.stringify(before) !== JSON.stringify(next) };
    } finally {
      draftActive = false;
      activeDraft = null;
    }
  }
  function applyFromHost(update) {
    const prepared = prepare(update);
    if (prepared.changed) assignData(prepared.captured.project, prepared.next);
    return prepared.result;
  }
  function execute(meta, update) {
    discardStaleGesture();
    if (gesture && !draftActive) throw new Error('Eine Systemgeste ist noch aktiv.');
    const prepared = prepare(update);
    if (prepared.nested || !prepared.changed) return prepared.result;
    const undo = ports.recordUndo(String(meta?.label || 'Systemaenderung'));
    assignData(prepared.captured.project, prepared.next);
    ports.completeUndo?.(undo);
    ports.commit(meta || {});
    return prepared.result;
  }
  function beginGesture(meta = {}) {
    if (ports.isExternallyBusy?.() || draftActive) throw new Error('Ein Systembefehl wird vorbereitet.');
    discardStaleGesture();
    if (gesture) throw new Error('Eine Systemgeste ist bereits aktiv.');
    if (!ports.authorize()) throw new Error('Keine Berechtigung fuer diese Systemaenderung.');
    const token = Object.freeze({});
    gesture = {
      token, captured: context(), meta: { ...meta },
      original: cloneSystemData(rawData()), undo: null, changed: false
    };
    return token;
  }
  function updateGesture(token, update) {
    discardStaleGesture();
    if (!gesture || gesture.token !== token || !isCurrent(gesture.captured)) return false;
    const prepared = prepare(update);
    if (prepared.changed) {
      if (!gesture.changed) gesture.undo = ports.recordUndo(String(gesture.meta.label || 'Systemgeste'));
      assignData(prepared.captured.project, prepared.next);
      ports.completeUndo?.(gesture.undo);
      gesture.changed = true;
    }
    return prepared.result;
  }
  function commitGesture(token) {
    if (!gesture || gesture.token !== token) return false;
    const completed = gesture;
    gesture = null;
    if (!isCurrent(completed.captured)) return false;
    if (completed.changed) { ports.completeUndo?.(completed.undo); ports.commit(completed.meta); }
    return completed.changed;
  }
  function cancelGesture(token) {
    if (!gesture || gesture.token !== token) return false;
    const cancelled = gesture;
    gesture = null;
    if (!isCurrent(cancelled.captured)) return false;
    if (cancelled.changed) {
      assignData(cancelled.captured.project, cloneSystemData(cancelled.original));
      ports.cancelUndo(cancelled.undo);
      ports.commit({ ...cancelled.meta, reason: 'system-gesture-cancelled' });
    }
    return true;
  }
  function assertIdle() {
    discardStaleGesture();
    if (gesture || draftActive) throw new Error('Eine Systemmutation ist bereits aktiv.');
  }
  function isIdle() { discardStaleGesture(); return !gesture && !draftActive; }
  return Object.freeze({ assertIdle, isIdle, read, execute, applyFromHost, beginGesture, updateGesture, commitGesture, cancelGesture });
}
